import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import PasswordProtection from "@/components/PasswordProtection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Alunos({ onLogout }) {
  const navigate = useNavigate();
  const [alunos, setAlunos] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [alunosRes, turmasRes] = await Promise.all([
        api.get('/alunos'),
        api.get('/turmas')
      ]);
      setAlunos(alunosRes.data);
      setTurmas(turmasRes.data);
    } catch (error) {
      // Erro já tratado pelo interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/alunos/${deleteId}`);
      toast.success("Aluno deletado com sucesso!");
      fetchData();
      setDeleteId(null);
    } catch (error) {
      toast.error("Erro ao deletar aluno");
    }
  };

  const getTurmasDoAluno = (aluno) => {
    // Suporte a migração de dados antigos
    let turmaIds = aluno.turma_ids || [];
    if (!turmaIds.length && aluno.turma_id) {
      turmaIds = [aluno.turma_id];
    }
    
    if (turmaIds.length === 0) {
      return [{ id: 'sem-turma', nome: 'Sem turma' }];
    }
    
    return turmaIds
      .map(turmaId => turmas.find(t => t.id === turmaId))
      .filter(t => t !== undefined);
  };

  // Filtrar e ordenar alunos alfabeticamente
  const filteredAlunos = alunos
    .filter(aluno => aluno.nome.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  if (loading) {
    return <Layout onLogout={onLogout}><div className="text-center py-12">Carregando...</div></Layout>;
  }

  return (
    <PasswordProtection page="alunos" onLogout={onLogout}>
      <Layout onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Alunos</h1>
            <p className="text-gray-600">Gerencie os alunos da escola</p>
          </div>
          <Button
            data-testid="novo-aluno-button"
            onClick={() => navigate('/alunos/novo')}
            className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 shadow-lg w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Aluno
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                data-testid="search-input"
                placeholder="Buscar por nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
          </CardHeader>
          <CardContent>
            {filteredAlunos.length === 0 ? (
              <div className="text-center py-12 text-gray-500" data-testid="empty-state">
                Nenhum aluno encontrado
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAlunos.map((aluno) => (
                    <Card key={aluno.id} className={`border hover:shadow-md transition-shadow ${aluno.status === 'inativo' ? 'opacity-70 bg-gray-50' : ''}`} data-testid={`aluno-card-${aluno.id}`}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-semibold text-base sm:text-lg break-words" data-testid={`aluno-nome-${aluno.id}`}>{aluno.nome}</h3>
                            {aluno.status === 'inativo' && (
                              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300" data-testid={`badge-inativo-${aluno.id}`}>
                                Inativo
                              </Badge>
                            )}
                          </div>
                          {aluno.status === 'inativo' && aluno.motivo_desligamento && (
                            <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800 break-words" data-testid={`motivo-desligamento-${aluno.id}`}>
                              <strong>Motivo do desligamento{aluno.data_desligamento ? ` (${new Date(aluno.data_desligamento).toLocaleDateString('pt-BR')})` : ''}:</strong> {aluno.motivo_desligamento}
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs sm:text-sm text-gray-600">
                            <div className="break-words"><strong>CPF:</strong> {aluno.cpf}</div>
                            <div className="break-words"><strong>Telefone:</strong> {aluno.telefone}</div>
                            <div className="break-words"><strong>Nascimento:</strong> {new Date(aluno.data_nascimento).toLocaleDateString('pt-BR')}</div>
                            <div className="break-words"><strong>Endereço:</strong> {aluno.endereco}</div>
                            <div className="md:col-span-2 break-words">
                              <strong>Responsável:</strong> {aluno.responsavel.nome} ({aluno.responsavel.grau_parentesco}) - {aluno.responsavel.telefone}
                            </div>
                            <div className="md:col-span-2">
                              <div className="flex flex-wrap gap-2">
                                {getTurmasDoAluno(aluno).map((turma, index) => (
                                  <Badge 
                                    key={turma.id} 
                                    variant={turma.id === 'sem-turma' ? 'outline' : 'secondary'}
                                    data-testid={`aluno-turma-${aluno.id}-${index}`}
                                  >
                                    {turma.nome}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                          <Button
                            data-testid={`edit-aluno-${aluno.id}`}
                            variant="outline"
                            size="icon"
                            onClick={() => navigate(`/alunos/editar/${aluno.id}`)}
                            className="h-9 w-9"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-aluno-${aluno.id}`}
                            variant="outline"
                            size="icon"
                            onClick={() => setDeleteId(aluno.id)}
                            className="text-red-600 hover:bg-red-50 h-9 w-9"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este aluno? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
    </PasswordProtection>
  );
}
